import { useState, useEffect } from 'react';
import DashboardLayout from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { QrCode, Camera, CheckCircle, Clock, Users, Loader2, X, ScanLine } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { motion } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { Scanner } from '@yudiel/react-qr-scanner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const StaffAttendanceQRPage = () => {
  const { token, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [todayAttendance, setTodayAttendance] = useState([]);
  const [stats, setStats] = useState({ present: 0, absent: 0, total: 0 });
  const [manualCode, setManualCode] = useState('');
  const [scannerOpen, setScannerOpen] = useState(false);
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    fetchTodayAttendance();
  }, []);

  const fetchTodayAttendance = async () => {
    try {
      const today = new Date().toISOString().split('T')[0];
      const response = await axios.get(`${API_URL}/api/hr/staff-attendance?date=${today}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const records = response.data || [];
      setTodayAttendance(records);
      
      // Get total staff count (teachers + non-teaching staff)
      const [teachersRes, staffRes] = await Promise.all([
        axios.get(`${API_URL}/api/teachers`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: { items: [] } })),
        axios.get(`${API_URL}/api/staff`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] }))
      ]);
      
      const teacherCount = teachersRes.data?.items?.length || teachersRes.data?.length || 0;
      const nonTeachingCount = staffRes.data?.items?.length || staffRes.data?.length || 0;
      const totalStaff = teacherCount + nonTeachingCount;
      
      const present = records.filter(r => r.status === 'present').length;
      setStats({ present, absent: Math.max(0, totalStaff - present), total: totalStaff });
    } catch (error) {
      console.error('Failed to fetch attendance:', error);
    } finally {
      setLoading(false);
    }
  };

  const markAttendance = async (code) => {
    if (processing) return;
    setProcessing(true);
    
    try {
      const response = await axios.post(`${API_URL}/api/hr/staff-attendance/qr-scan`, null, {
        params: { staff_code: code.trim().toUpperCase() },
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(response.data.message || 'Attendance marked successfully');
      setManualCode('');
      setScannerOpen(false);
      fetchTodayAttendance();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to mark attendance');
    } finally {
      setProcessing(false);
    }
  };

  const handleManualEntry = () => {
    if (!manualCode.trim()) {
      toast.error('Please enter staff code');
      return;
    }
    markAttendance(manualCode);
  };

  const handleQRScan = (result) => {
    if (result && result[0]?.rawValue && !processing) {
      const scannedCode = result[0].rawValue;
      markAttendance(scannedCode);
    }
  };

  const getStatusBadge = (status) => {
    if (status === 'present') return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
    if (status === 'late') return 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200';
    if (status === 'half_day') return 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200';
    if (status === 'absent') return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
    return 'bg-gray-100 text-gray-800';
  };

  if (loading) {
    return (
      <DashboardLayout title="Staff QR Attendance">
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Staff QR Attendance">
      <div className="space-y-6">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <Card>
              <CardContent className="pt-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Total Staff</p>
                    <p className="text-2xl font-bold">{stats.total}</p>
                  </div>
                  <Users className="h-8 w-8 text-blue-500" />
                </div>
              </CardContent>
            </Card>
          </motion.div>
          
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
            <Card>
              <CardContent className="pt-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Present Today</p>
                    <p className="text-2xl font-bold text-green-600">{stats.present}</p>
                  </div>
                  <CheckCircle className="h-8 w-8 text-green-500" />
                </div>
              </CardContent>
            </Card>
          </motion.div>
          
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
            <Card>
              <CardContent className="pt-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Yet to Arrive</p>
                    <p className="text-2xl font-bold text-orange-600">{stats.absent}</p>
                  </div>
                  <Clock className="h-8 w-8 text-orange-500" />
                </div>
              </CardContent>
            </Card>
          </motion.div>
          
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}>
            <Card className="bg-gradient-to-r from-blue-500 to-indigo-600 text-white">
              <CardContent className="pt-4">
                <div className="text-center">
                  <p className="text-blue-100">Today's Date</p>
                  <p className="text-xl font-bold">{new Date().toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short' })}</p>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </div>

        {/* QR Scanner Section */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <QrCode className="h-5 w-5" />
              QR Code Scanner
            </CardTitle>
            <CardDescription>Scan staff QR code or enter code manually</CardDescription>
          </CardHeader>
          <CardContent>
            <Tabs defaultValue="manual">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="manual" data-testid="manual-entry-tab">Manual Entry</TabsTrigger>
                <TabsTrigger value="camera" data-testid="camera-scan-tab">Camera Scan</TabsTrigger>
              </TabsList>
              
              <TabsContent value="manual" className="space-y-4">
                <div className="flex gap-4">
                  <Input
                    placeholder="Enter Staff Code (e.g., STAFF001)"
                    value={manualCode}
                    onChange={(e) => setManualCode(e.target.value.toUpperCase())}
                    onKeyPress={(e) => e.key === 'Enter' && handleManualEntry()}
                    className="flex-1"
                    data-testid="staff-code-input"
                  />
                  <Button onClick={handleManualEntry} disabled={processing} data-testid="mark-attendance-btn">
                    {processing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CheckCircle className="h-4 w-4 mr-2" />}
                    Mark Attendance
                  </Button>
                </div>
                <p className="text-sm text-muted-foreground">
                  Enter the staff member's unique code to mark their attendance
                </p>
              </TabsContent>
              
              <TabsContent value="camera" className="space-y-4">
                <div className="text-center">
                  <Button onClick={() => setScannerOpen(true)} size="lg" data-testid="open-scanner-btn">
                    <Camera className="h-5 w-5 mr-2" /> Open Camera Scanner
                  </Button>
                  <p className="text-sm text-muted-foreground mt-2">
                    Point your camera at the staff member's QR code
                  </p>
                </div>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>

        {/* Today's Attendance */}
        <Card>
          <CardHeader>
            <CardTitle>Today's Attendance Log</CardTitle>
          </CardHeader>
          <CardContent>
            {todayAttendance.length === 0 ? (
              <p className="text-center text-muted-foreground py-8">No attendance records yet today</p>
            ) : (
              <div className="space-y-2">
                {todayAttendance.map((record, idx) => (
                  <motion.div
                    key={idx}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: idx * 0.05 }}
                    className="flex items-center justify-between p-3 bg-muted/50 rounded-lg"
                    data-testid={`attendance-record-${idx}`}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
                        <Users className="w-5 h-5 text-primary" />
                      </div>
                      <div>
                        <p className="font-medium">{record.staff_name || 'Staff Member'}</p>
                        <p className="text-sm text-muted-foreground">
                          In: {record.check_in_time || record.check_in || 'N/A'} 
                          {(record.check_out_time || record.check_out) && ` | Out: ${record.check_out_time || record.check_out}`}
                          {record.late_minutes > 0 && <span className="text-amber-600 ml-2">(Late: {record.late_minutes} min)</span>}
                          {record.overtime_minutes > 0 && <span className="text-green-600 ml-2">(OT: {record.overtime_minutes} min)</span>}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge className={getStatusBadge(record.status)}>
                        {record.status === 'half_day' ? 'Half Day' : record.status?.charAt(0).toUpperCase() + record.status?.slice(1)}
                      </Badge>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Camera Scanner Dialog */}
        <Dialog open={scannerOpen} onOpenChange={setScannerOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center justify-between">
                <span className="flex items-center gap-2">
                  <ScanLine className="h-5 w-5" /> QR Scanner
                </span>
                <Button variant="ghost" size="sm" onClick={() => setScannerOpen(false)}>
                  <X className="h-4 w-4" />
                </Button>
              </DialogTitle>
            </DialogHeader>
            <div className="relative">
              <Scanner
                onScan={handleQRScan}
                onError={(err) => console.error('Scanner error:', err)}
                styles={{
                  container: { borderRadius: '8px', overflow: 'hidden' },
                  video: { borderRadius: '8px' }
                }}
                components={{
                  audio: false
                }}
              />
              {processing && (
                <div className="absolute inset-0 bg-black/50 flex items-center justify-center rounded-lg">
                  <Loader2 className="h-8 w-8 animate-spin text-white" />
                </div>
              )}
            </div>
            <p className="text-center text-sm text-muted-foreground">
              Position the QR code within the frame
            </p>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default StaffAttendanceQRPage;
